import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  message as antMessage, 
  Spin, 
  Layout, 
  Modal, 
  Typography, 
  Input,
  Badge,
  Button,
  List,
  Card,
  Space
} from 'antd';
import { LoadingOutlined, DeleteOutlined } from '@ant-design/icons';
import ServiceHeader from './components/ServiceHeader';
import ServiceImageSection from './components/ServiceImageSection';
import ServiceDetailsSection from './components/ServiceDetailsSection';
import ContactsSection from './components/ContactsSection';
import ReviewsSection from './components/ReviewsSection';
import BookingSection from './components/BookingSection';
import InquirySection from './components/InquirySection';
import { useAuth } from '../../context/DBContext';
import { createOneTimeCheckoutSession } from '../../api/stripe';
import moment from 'moment';

const { Content } = Layout;
const { confirm } = Modal;
const { Text, Title } = Typography;
const { TextArea } = Input;
const PAGE_SIZE = 5; // Number of reviews per page

const ServiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [message, setMessage] = useState("");
  const { 
    userInfo, 
    fetchServiceById, 
    getUserInfo, 
    sendMessage, 
    currentUser, 
    ensureConversationExists, 
    fetchTimeSlotsWithBookings, 
    getTimeSlotAvailability, 
    addReviewToService, 
    fetchReviewsForService,
    deleteServiceReview,
    deleteService 
  } = useAuth();
  const[loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(moment()); // Initialize to today
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [timeSlotsByDate, setTimeSlotsByDate] = useState({});
  const [reviews, setReviews] = useState([]);
  const [userRating, setUserRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReviews, setTotalReviews] = useState(0);
  const [isChildModalVisible, setIsChildModalVisible] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const {claimServiceForBusiness} = useAuth();
  
  const claimService = () => {
    confirm({
      title: 'Do you want to claim this service?',
      content: 'Confirming will mark this service as claimed by your business.',
      okText: 'Claim',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await claimServiceForBusiness(id, currentUser.uid);
          setService(prev => ({ ...prev, claimed: true, claimedBy: currentUser.uid }));
          antMessage.success('Claim request submitted successfully. Waiting for admin approval.');
        } catch (error) {
          // console.error('Error claiming service:', error);
          antMessage.error(`Error submitting claim request. Please contact admin.`);
        }
      }
    });
  };

  const handleSendMessage = async () => {
    if (message) {
      try {
        // console.log("message being send... " );
        // Ensure we are passing the recipientName as required
        await ensureConversationExists(currentUser.uid, service.claimedBy, service.name);

        await sendMessage(currentUser.uid, service.name, service.claimedBy, message);
        setMessage('');    
        navigate('/chat', { state: { recipientId: service.claimedBy} }); // Navigate with state

      } catch (error) {
        // console.error('Error sending message:', error);
      }
    }
  };

  // console.log('Role:', userRole, 'Service Claimed:', service?.claimed);
  
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      if (currentUser) {
        try {
          const [serviceData, userInfo] = await Promise.all([
            fetchServiceById(id),
            getUserInfo(currentUser.uid),
          ]);
  
          setService(serviceData);
          setUserRole(userInfo?.role);
  
          // Fetch time slots for the service
          const timeSlotsData = await fetchTimeSlotsWithBookings(id);
  
          // Organize time slots by date for the calendar
          const slotsByDate = {};
          timeSlotsData.forEach(slot => {
            const date = slot.date; // Assuming slot.date is in 'YYYY-MM-DD' format
            if (!slotsByDate[date]) {
              slotsByDate[date] = [];
            }
            slotsByDate[date].push(slot);
          });
  
          setTimeSlotsByDate(slotsByDate);
          setTimeSlots(timeSlotsData);
  
          // Set selectedDate to today and availableTimeSlots accordingly
          const todayStr = moment().format('YYYY-MM-DD');
          setSelectedDate(moment()); // Ensure selectedDate is a moment object
          setAvailableTimeSlots(slotsByDate[todayStr] || []);
  
        } catch (error) {
          antMessage.error('Failed to load service details.');
          // console.error('Error:', error);
        } finally {
          setLoading(false);
        }
      }
    }
    fetchData();
  }, []);
  

   // Sample Time Slots Data
   const sampleTimeSlots = {
    '2024-04-25': [
      { id: 'ts1', startTime: '09:00 AM', endTime: '10:00 AM', availability: 5, price: 50 },
      { id: 'ts2', startTime: '11:00 AM', endTime: '12:00 PM', availability: 2, price: 60 },
    ],
    '2024-04-26': [
      { id: 'ts3', startTime: '01:00 PM', endTime: '02:00 PM', availability: 0, price: 55 },
      { id: 'ts4', startTime: '03:00 PM', endTime: '04:00 PM', availability: 3, price: 65 },
    ],
    // Add more dates and time slots as needed
  };

  // Handle date selection from the calendar
  const onSelectDate = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    setSelectedDate(dateStr);
    const slotsForDate = timeSlotsByDate[dateStr] || [];
    setAvailableTimeSlots(slotsForDate);
  };
  
  const dateCellRender = (value) => {
    const dateStr = value.format('YYYY-MM-DD');
    const slotsForDate = timeSlotsByDate[dateStr] || [];
    return slotsForDate.length > 0 ? (
      <Badge count={slotsForDate.length} style={{ backgroundColor: '#52c41a' }} />
    ) : null;
  };
  
  
  // Handle booking a time slot
  const handleBookNow = async (timeSlot) => {
    try {
      if (!currentUser) {
        antMessage.error('Please sign in to book a service.');
        navigate('/');
        return;
      }
  
      if (userInfo?.accountType === 'children') {
        setSelectedTimeSlot(timeSlot);
        setIsChildModalVisible(true);
      } else {
        setIsCheckoutLoading(true);
        // Create a checkout session - this will handle the redirect internally
        await createOneTimeCheckoutSession(currentUser, id, timeSlot.id);
        // Add delay to account for Stripe checkout popup
        await new Promise(resolve => setTimeout(resolve, 10000));
      }
    } catch (error) {
      // console.error('Error initiating booking:', error);
      antMessage.error(error.message || 'Failed to initiate booking.');
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const handleChildSelect = async (childId) => {
    try {
      setIsCheckoutLoading(true);
      setIsChildModalVisible(false);
      // Create a checkout session - this will handle the redirect internally
      await createOneTimeCheckoutSession(currentUser, id, selectedTimeSlot.id, childId);
      // Add delay to account for Stripe checkout popup
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // console.error('Error creating checkout session:', error);
      antMessage.error('Failed to create checkout session');
    } finally {
      setIsCheckoutLoading(false);
    }
  };
  
  const { addFavoriteService, removeFavoriteService } = useAuth(); // Destructure the new functions

  const [isFavorite, setIsFavorite] = useState(userInfo?.favoriteServices?.includes(id));
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const toggleFavorite = async () => {
    if (!currentUser) {
      antMessage.error('Please sign in to favorite services.');
      navigate('/signup');
      return;
    }
  
    const newFavoriteStatus = !isFavorite;
    setIsFavorite(newFavoriteStatus); // Optimistically update UI
    setFavoriteLoading(true); // Start favorite loading
  
    try {
      if (newFavoriteStatus) {
        await addFavoriteService(userInfo.id, id);
        antMessage.success('Service added to favorites.');
      } else {
        await removeFavoriteService(userInfo.id, id);
        antMessage.success('Service removed from favorites.');
      }
    } catch (error) {
      setIsFavorite(!newFavoriteStatus); // Revert UI if there was an error
      antMessage.error('Failed to update favorites.');
    } finally {
      setFavoriteLoading(false); // End favorite loading
    }
  };
  
  useEffect(() => {
    if (id) {
      const unsubscribe = fetchReviewsForService(id, (fetchedReviews) => {
        setTotalReviews(fetchedReviews.length);
        // Calculate paginated reviews
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        const paginatedReviews = fetchedReviews.slice(startIndex, startIndex + PAGE_SIZE);
        setReviews(paginatedReviews);
      });
      return () => unsubscribe();
    }
  }, [id, currentPage]);

  const handleReviewSubmit = async () => {
    if (!currentUser) {
      antMessage.error('Please sign in to leave a review');
      return;
    }

    if (userRating === 0) {
      antMessage.error('Please select a rating');
      return;
    }

    setSubmittingReview(true);
    try {
      await addReviewToService(id, userRating, reviewComment);
      antMessage.success('Review submitted successfully');
      setUserRating(0);
      setReviewComment('');
    } catch (error) {
      antMessage.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await deleteServiceReview(id, reviewId);
      antMessage.success('Review deleted successfully');
    } catch (error) {
      // console.error('Error deleting review:', error);
      antMessage.error('Failed to delete review');
    }
  };

  const handleDeleteService = () => {
    Modal.confirm({
      title: 'Are you sure you want to delete this service?',
      content: (
        <div>
          <p><strong>{service?.name}</strong></p>
          <p style={{ color: 'red' }}>Warning: This will permanently delete:</p>
          <ul style={{ color: 'red' }}>
            <li>All time slots and bookings</li>
            <li>All reviews</li>
            <li>Remove from all users' favorites</li>
            <li>All business claim records</li>
          </ul>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, delete it',
      okType: 'danger',
      cancelText: 'No, keep it',
      onOk: async () => {
        try {
          await deleteService(id);
          antMessage.success('Service deleted successfully');
          navigate('/service'); // Redirect to service listing page
        } catch (error) {
          console.error('Error deleting service:', error);
          // antMessage.error('Failed to delete service');
        }
      }
    });
  };
  
    return (
      <>
      {service ? (
        <section>
  <div className="container-fluid">
            <ServiceHeader 
              userRole={userRole} 
              service={service} 
              onClaimService={claimService}
              onDeleteService={handleDeleteService}
            />
    <div className="row clearfix">
      <div className="col-lg-8 col-md-12">
                <ServiceImageSection 
                  service={service}
                  isFavorite={isFavorite}
                  onToggleFavorite={toggleFavorite}
                />
                <ServiceDetailsSection service={service} />
                <ContactsSection service={service} />
                <ReviewsSection 
                  reviews={reviews}
                  currentPage={currentPage}
                  totalReviews={totalReviews}
                  PAGE_SIZE={PAGE_SIZE}
                  userRating={userRating}
                  reviewComment={reviewComment}
                  currentUser={currentUser}
                  submittingReview={submittingReview}
                  onPageChange={setCurrentPage}
                  onRatingChange={setUserRating}
                  onCommentChange={(e) => setReviewComment(e.target.value)}
                  onReviewSubmit={handleReviewSubmit}
                  isAdmin={userInfo?.role === 'admin'}
                  onDeleteReview={handleDeleteReview}
                />
                  </div>
              <div className="col-lg-4 col-md-12">
                {service && service.claimed && (
                  <BookingSection 
                    service={service}
                    selectedDate={selectedDate}
                    loadingTimeSlots={loadingTimeSlots}
                    availableTimeSlots={availableTimeSlots}
                    onSelectDate={onSelectDate}
                    dateCellRender={dateCellRender}
                    onBookNow={handleBookNow}
                    isCheckoutLoading={isCheckoutLoading}
                  />
                )}
                <InquirySection 
                  service={service}
                  message={message}
                  onMessageChange={setMessage}
                  onSendMessage={handleSendMessage}
                />
              </div>
               </div>
          </div>
          <Modal
            title="Select Child"
            open={isChildModalVisible}
            onCancel={() => !isCheckoutLoading && setIsChildModalVisible(false)}
            footer={null}
            closable={!isCheckoutLoading}
            maskClosable={!isCheckoutLoading}
          >
            <List
              dataSource={userInfo?.children || []}
              renderItem={(child) => (
                <List.Item>
                  <div className="d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      {`${child.firstName} ${child.lastName}`}
                    </Typography.Title>
                    <Button 
                      type="primary" 
                      onClick={() => handleChildSelect(child.id)}
                      loading={isCheckoutLoading}
                      disabled={isCheckoutLoading}
                    >
                      Select
                    </Button>
                  </div>
                </List.Item>
              )}
            />
          </Modal>
        </section>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      )}
      </>
    );
  }
  
  export default ServiceDetailPage;
  